import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import SaveIcon from '@material-ui/icons/Save';

import TopToolbar from '../../global/subcomponents/topToolbar/TopToolbar';
import { INK, GREEN, RED, CANVAS, LINE, MUTED, CARD_SHADOW } from './tokens';
import {
  uid,
  parseHHMM,
  minutesToHHMM,
  overlapWarnings,
} from './bonusSchedule';
import {
  Card,
  GhostButton,
  StatusSelector,
  DayChips,
  TimePicker12,
  DurationStepper,
  ScheduleResult,
  ConfirmDialog,
} from './bonusUi';

const MAX_NAME = 60;

// "Pay to customer" accepts digits and a single decimal point only — no
// letters/symbols — capped at two decimal places.
function sanitizeAmount(raw) {
  let v = String(raw).replace(/[^\d.]/g, '');
  const firstDot = v.indexOf('.');
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    const [intPart, decPart] = v.split('.');
    v = `${intPart}.${decPart.slice(0, 2)}`;
  }
  return v;
}

/* ---------- working-model <-> server-model conversion ---------- */

function toWorkingBlock(b) {
  return {
    id: uid(),
    days: (b.days || []).slice(),
    allDay: !!b.allDay,
    startMin: b.startTime != null ? parseHHMM(b.startTime) : 15 * 60,
    durationMin: b.durationMinutes != null ? b.durationMinutes : 60,
  };
}

function freshBlock() {
  return { id: uid(), days: [], allDay: false, startMin: 15 * 60, durationMin: 60 };
}

function freshPayout() {
  return { id: uid(), description: '', amount: '' };
}

class EditBonus extends React.Component {
  constructor(props) {
    super(props);
    const b = props.editBonus;
    if (b) {
      this.state = {
        manageBonusId: b.manageBonusId || '',
        name: b.name || '',
        active: b.enabled !== false,
        createdBy: b.createdBy || '',
        createdByName: b.createdByName || '',
        payouts:
          (b.specifications || []).length > 0
            ? b.specifications.map((p) => ({
                id: uid(),
                description: p.description || '',
                amount: p.amount != null ? String(p.amount) : '',
              }))
            : [freshPayout()],
        blocks:
          (b.blocks || []).length > 0
            ? b.blocks.map(toWorkingBlock)
            : [freshBlock()],
      };
    } else {
      this.state = {
        manageBonusId: '',
        name: '',
        active: true,
        createdBy: props.user.userId || '',
        createdByName:
          props.user.displayName ||
          `${props.user.firstname || ''} ${props.user.lastname || ''}`.trim(),
        payouts: [freshPayout()],
        blocks: [freshBlock()],
      };
    }
  }

  /* ---------- payout handlers ---------- */

  updatePayout = (id, field, value) =>
    this.setState((s) => ({
      payouts: s.payouts.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
    }));

  addPayout = () =>
    this.setState((s) => ({ payouts: [...s.payouts, freshPayout()] }));

  removePayout = (id) =>
    this.setState((s) => ({
      payouts:
        s.payouts.length > 1 ? s.payouts.filter((p) => p.id !== id) : s.payouts,
    }));

  /* ---------- block handlers ---------- */

  updateBlock = (id, patch) =>
    this.setState((s) => ({
      blocks: s.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    }));

  addBlock = () => this.setState((s) => ({ blocks: [...s.blocks, freshBlock()] }));

  removeBlock = (id) =>
    this.setState((s) => ({
      blocks: s.blocks.length > 1 ? s.blocks.filter((b) => b.id !== id) : s.blocks,
    }));

  toggleDay = (id, dayIdx) =>
    this.setState((s) => ({
      blocks: s.blocks.map((b) => {
        if (b.id !== id) return b;
        const days = b.days.includes(dayIdx)
          ? b.days.filter((d) => d !== dayIdx)
          : [...b.days, dayIdx].sort((a, c) => a - c);
        return { ...b, days };
      }),
    }));

  toggleAllDay = (id) =>
    this.setState((s) => ({
      blocks: s.blocks.map((b) => {
        if (b.id !== id) return b;
        return b.allDay
          ? { ...b, allDay: false, startMin: 15 * 60, durationMin: 60 }
          : { ...b, allDay: true };
      }),
    }));

  /* ---------- validation (spec §10) ---------- */

  validate = () => {
    const { name, payouts, blocks } = this.state;
    const errors = { payouts: {}, blocks: {} };
    if (!name.trim()) errors.name = 'Enter a bonus name.';

    let hasPayout = false;
    payouts.forEach((p) => {
      const d = p.description.trim();
      const a = p.amount.trim();
      if (!d && !a) return;
      hasPayout = true;
      if (!d) errors.payouts[p.id] = 'Enter a description or remove the row.';
      else if (!a || Number(a) <= 0)
        errors.payouts[p.id] = 'Enter an amount or remove the row.';
    });
    if (!hasPayout) errors.noPayout = 'Add at least one payout.';

    blocks.forEach((b) => {
      if (!b.days || b.days.length === 0)
        errors.blocks[b.id] = 'Select at least one day.';
    });

    errors.valid =
      !errors.name &&
      !errors.noPayout &&
      Object.keys(errors.payouts).length === 0 &&
      Object.keys(errors.blocks).length === 0;
    return errors;
  };

  buildPayload = () => {
    const { manageBonusId, name, active, createdBy, createdByName, payouts, blocks } =
      this.state;
    return {
      manageBonusId,
      locationId: this.props.user.selectedLocationId,
      name: name.trim().toUpperCase(),
      enabled: active,
      createdBy,
      createdByName,
      specifications: payouts
        .filter((p) => p.description.trim() || p.amount.trim())
        .map((p, i) => ({
          description: p.description.trim(),
          amount: p.amount.trim(),
          sortOrder: i,
        })),
      blocks: blocks.map((b, i) => ({
        days: b.days,
        allDay: b.allDay,
        startTime: b.allDay ? null : minutesToHHMM(b.startMin),
        durationMinutes: b.allDay ? null : b.durationMin,
        sortOrder: i,
      })),
    };
  };

  handleSave = () => {
    const errors = this.validate();
    if (!errors.valid) {
      this.props.openFeedbackBar(
        'error',
        errors.name || errors.noPayout || 'Please fix the highlighted fields.'
      );
      this.setState({ showErrors: true });
      return;
    }
    this.props.saveBonus(
      this.buildPayload(),
      (err, message) => {
        if (err) this.props.openFeedbackBar('error', message);
        else {
          this.props.openFeedbackBar('success', 'Bonus saved');
          this.props.handleEditingClear();
        }
      }
    );
  };

  handleDelete = () => {
    if (!this.state.manageBonusId) {
      this.props.handleEditingClear();
      return;
    }
    this.props.handleDelete(this.state.manageBonusId);
    this.props.handleEditingClear();
  };

  render() {
    const { name, active, payouts, blocks, showErrors } = this.state;
    const readOnly = this.props.readOnly;
    const errors = showErrors ? this.validate() : { payouts: {}, blocks: {} };
    const isNew = !this.state.manageBonusId;
    const overlaps = overlapWarnings(
      blocks.map((b) => ({
        days: b.days,
        allDay: b.allDay,
        startTime: minutesToHHMM(b.startMin),
        durationMinutes: b.durationMin,
      }))
    );

    const menuItems = [
      {
        title: 'Back to Menu',
        icon: <KeyboardBackspaceIcon />,
        onClick: this.props.handleEditingClear,
      },
    ];
    if (!readOnly) {
      menuItems.push({
        title: 'Save',
        icon: <SaveIcon />,
        onClick: this.handleSave,
      });
    }
    const pageName = readOnly ? 'View bonus' : isNew ? 'New bonus' : 'Edit bonus';

    return (
      <div className="nds-bonus" style={{ background: CANVAS, minHeight: '100%' }}>
        <TopToolbar pageName={pageName} menuItems={menuItems} />

        <div className="nds-bonus-page">
          {readOnly && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: '#fff',
                borderRadius: 16,
                padding: '12px 16px',
                marginBottom: 16,
                color: MUTED,
                boxShadow: CARD_SHADOW,
              }}
            >
              <LockOutlinedIcon style={{ fontSize: 18 }} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                Read-only — only the creator can edit this bonus.
              </span>
            </div>
          )}

          {/* Identity */}
          <div
            style={{
              borderRadius: 24,
              background: '#fff',
              boxShadow: CARD_SHADOW,
              padding: '16px 20px 8px',
              marginBottom: 16,
            }}
          >
            <p style={{ fontSize: 12, fontWeight: 700, color: MUTED, margin: '0 0 4px' }}>
              Name of the bonus
            </p>
            <input
              value={name}
              disabled={readOnly}
              onChange={(e) =>
                this.setState({ name: e.target.value.toUpperCase().slice(0, MAX_NAME) })
              }
              placeholder="SOLID 777 (0.75C)"
              autoCapitalize="characters"
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: '-0.01em',
                color: INK,
                textTransform: 'uppercase',
                paddingBottom: 14,
              }}
            />
            {errors.name && (
              <p style={{ color: RED, fontSize: 12, margin: '0 0 8px', fontWeight: 600 }}>
                {errors.name}
              </p>
            )}
            <div style={{ padding: '14px 0 4px', borderTop: `1px solid ${LINE}` }}>
              <StatusSelector
                active={active}
                onChange={(v) => !readOnly && this.setState({ active: v })}
              />
            </div>
          </div>

          <div className="nds-bonus-edit-grid">
          {/* Payouts */}
          <Card title="Payouts">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0 12px',
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  flex: '1 1 0%',
                  minWidth: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  color: MUTED,
                }}
              >
                Description
              </span>
              <span
                style={{
                  flex: '0 0 108px',
                  width: 108,
                  textAlign: 'right',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: MUTED,
                  whiteSpace: 'nowrap',
                }}
              >
                Pay to customer
              </span>
              <span style={{ flex: '0 0 24px', width: 24 }} />
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              {payouts.map((p) => (
                <div key={p.id}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      borderRadius: 16,
                      background: CANVAS,
                      padding: '8px 8px 8px 12px',
                    }}
                  >
                    <div
                      style={{
                        flex: '1 1 0%',
                        minWidth: 0,
                        background: '#fff',
                        border: `1px solid ${LINE}`,
                        borderRadius: 10,
                        padding: '8px 12px',
                      }}
                    >
                      <input
                        value={p.description}
                        disabled={readOnly}
                        placeholder="50 SPINS"
                        onChange={(e) =>
                          this.updatePayout(p.id, 'description', e.target.value)
                        }
                        style={{
                          width: '100%',
                          minWidth: 0,
                          border: 'none',
                          outline: 'none',
                          background: 'transparent',
                          fontSize: 16,
                          fontWeight: 700,
                          color: INK,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        flex: '0 0 108px',
                        width: 108,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        background: '#fff',
                        border: `1px solid ${LINE}`,
                        borderRadius: 10,
                        padding: '8px',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700, color: MUTED }}>
                        $
                      </span>
                      <input
                        value={p.amount}
                        disabled={readOnly}
                        inputMode="decimal"
                        placeholder="0"
                        onChange={(e) =>
                          this.updatePayout(p.id, 'amount', sanitizeAmount(e.target.value))
                        }
                        style={{
                          width: '100%',
                          minWidth: 0,
                          border: 'none',
                          outline: 'none',
                          background: 'transparent',
                          fontSize: 14,
                          fontWeight: 700,
                          textAlign: 'right',
                          color: INK,
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      disabled={readOnly || payouts.length <= 1}
                      onClick={() => this.removePayout(p.id)}
                      style={{
                        flex: '0 0 24px',
                        width: 24,
                        height: 24,
                        border: 'none',
                        background: 'transparent',
                        cursor:
                          readOnly || payouts.length <= 1 ? 'default' : 'pointer',
                        color: MUTED,
                        opacity: payouts.length <= 1 ? 0.3 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CloseIcon style={{ fontSize: 16 }} />
                    </button>
                  </div>
                  {errors.payouts[p.id] && (
                    <p
                      style={{
                        color: RED,
                        fontSize: 12,
                        margin: '4px 0 0 12px',
                        fontWeight: 600,
                      }}
                    >
                      {errors.payouts[p.id]}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {errors.noPayout && (
              <p style={{ color: RED, fontSize: 12, margin: '8px 0 0', fontWeight: 600 }}>
                {errors.noPayout}
              </p>
            )}
            {!readOnly && <GhostButton onClick={this.addPayout}>Add payout</GhostButton>}
          </Card>

          {/* Schedule */}
          <Card title="Schedule">
            <div style={{ display: 'grid', gap: 12 }}>
              {blocks.map((b, i) => (
                <TimeBlockPanel
                  key={b.id}
                  index={i}
                  block={b}
                  readOnly={readOnly}
                  error={errors.blocks[b.id]}
                  onToggleDay={(d) => this.toggleDay(b.id, d)}
                  onToggleAllDay={() => this.toggleAllDay(b.id)}
                  onStartChange={(startMin) => this.updateBlock(b.id, { startMin })}
                  onDurationChange={(durationMin) =>
                    this.updateBlock(b.id, { durationMin })
                  }
                  onRemove={blocks.length > 1 ? () => this.removeBlock(b.id) : null}
                />
              ))}
            </div>
            {overlaps.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {overlaps.map((w) => (
                  <p
                    key={w}
                    style={{
                      color: '#B45309',
                      fontSize: 12,
                      margin: '2px 0',
                      fontWeight: 600,
                    }}
                  >
                    {w}
                  </p>
                ))}
              </div>
            )}
            {!readOnly && (
              <GhostButton onClick={this.addBlock}>Add time block</GhostButton>
            )}
          </Card>
          </div>

          {!readOnly && !isNew && (
            <button
              type="button"
              onClick={() => this.setState({ confirmDelete: true })}
              style={{
                width: '100%',
                padding: '14px 0',
                borderRadius: 16,
                border: 'none',
                cursor: 'pointer',
                background: '#fff',
                color: '#D92D53',
                fontSize: 14,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: CARD_SHADOW,
                marginBottom: 16,
              }}
            >
              <DeleteOutlineIcon style={{ fontSize: 18 }} />
              Delete bonus
            </button>
          )}

          {!readOnly && (
            <button
              type="button"
              onClick={this.handleSave}
              style={{
                width: '100%',
                padding: '16px 0',
                borderRadius: 16,
                border: 'none',
                cursor: 'pointer',
                background: INK,
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                boxShadow: '0 8px 20px rgba(11,19,84,0.25)',
              }}
            >
              Save bonus
            </button>
          )}
        </div>

        <ConfirmDialog
          open={!!this.state.confirmDelete}
          title="Delete bonus?"
          message={`This permanently removes this bonus and its payouts and schedule. This can't be undone.`}
          highlight={this.state.name ? `"${this.state.name}"` : ''}
          confirmLabel="Delete bonus"
          onCancel={() => this.setState({ confirmDelete: false })}
          onConfirm={() => {
            this.setState({ confirmDelete: false });
            this.handleDelete();
          }}
        />
      </div>
    );
  }
}

/* ---------- Time block panel ---------- */

function TimeBlockPanel({
  index,
  block,
  readOnly,
  error,
  onToggleDay,
  onToggleAllDay,
  onStartChange,
  onDurationChange,
  onRemove,
}) {
  const { days, allDay, startMin, durationMin } = block;
  const label = { fontSize: 12, fontWeight: 600, color: MUTED, margin: '0 0 10px' };

  const resultBlock = {
    days,
    allDay,
    startTime: minutesToHHMM(startMin),
    durationMinutes: durationMin,
  };

  return (
    <div style={{ borderRadius: 16, background: CANVAS, padding: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 18,
        }}
      >
        <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: INK }}>
          Block {index + 1}
        </h3>
        {onRemove && !readOnly && (
          <button
            type="button"
            onClick={onRemove}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
              color: MUTED,
            }}
          >
            Remove
          </button>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <p style={label}>Starts on</p>
        <DayChips days={days} onToggle={readOnly ? () => {} : onToggleDay} />
        {error && (
          <p style={{ color: RED, fontSize: 12, margin: '8px 0 0', fontWeight: 600 }}>
            {error}
          </p>
        )}
      </div>

      <div style={{ marginBottom: allDay ? 0 : 20 }}>
        <p style={label}>Starts at</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {!allDay && (
            <TimePicker12
              startMin={startMin}
              onChange={readOnly ? () => {} : onStartChange}
            />
          )}
          <button
            type="button"
            onClick={readOnly ? undefined : onToggleAllDay}
            style={{
              padding: '10px 16px',
              borderRadius: 999,
              cursor: readOnly ? 'default' : 'pointer',
              fontSize: 13,
              fontWeight: 700,
              background: allDay ? GREEN : '#fff',
              color: allDay ? '#fff' : MUTED,
              border: `1px solid ${allDay ? GREEN : LINE}`,
            }}
          >
            All day
          </button>
        </div>
        {allDay && (
          <p style={{ fontSize: 13, fontWeight: 500, color: MUTED, margin: '10px 0 0' }}>
            No start or end time — the bonus runs the whole day.
          </p>
        )}
      </div>

      {!allDay && (
        <div style={{ marginBottom: 20 }}>
          <p style={label}>Runs for</p>
          <DurationStepper
            durationMin={durationMin}
            onChange={readOnly ? () => {} : onDurationChange}
          />
        </div>
      )}

      <ScheduleResult block={resultBlock} />
    </div>
  );
}

export default EditBonus;
